import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, RefreshCw, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { palette, admin, fontFamily } from '../../styles/tokens';
import { formatMoney, canonicalMoney, sumMoney } from '../../lib/money';
import type { TeamMember } from '../../types';
import { log } from '../../lib/logger';

interface Settlement {
  id: string;
  operator_id: string;
  period_start: string;
  period_end: string;
  total_services: number;
  total_tips: number;
  house_commission: number;
  house_tip_share: number;
  operator_earnings: number;
  chair_rent: number;
  net_to_operator: number;
  net_to_house: number;
  completed_bookings: number;
  status: 'draft' | 'confirmed' | 'paid';
}

interface OperatorRow {
  id: string;
  name: string;
  services: number;
  revenue: number;
  tips: number;
  houseCommission: number;
  houseTipShare: number;
  chairRent: number;
  netToOperator: number;
  netToHouse: number;
  status: string;
  settlementId: string | null;
}

type Period = 'this_week' | 'last_week' | 'this_month' | 'custom';

function getPeriodRange(period: Period): { start: string; end: string } {
  const now = new Date();
  if (period === 'this_week') {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday.toISOString().split('T')[0], end: sunday.toISOString().split('T')[0] };
  }
  if (period === 'last_week') {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday.toISOString().split('T')[0], end: sunday.toISOString().split('T')[0] };
  }
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: first.toISOString().split('T')[0], end: last.toISOString().split('T')[0] };
}

function fmtCurrency(n: number): string {
  return formatMoney(n);
}

const LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: '0.5px' };
const CELL: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: palette.g700 };
const HEADER_CELL: React.CSSProperties = { ...CELL, fontWeight: 600, color: palette.g500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' };

interface Props {
  businessId: string;
  userId: string;
  operators: TeamMember[];
}

export function CommissionsTab({ businessId, userId: _userId, operators }: Props) {
  const [period, setPeriod] = useState<Period>('this_week');
  const [rows, setRows] = useState<OperatorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!businessId || operators.length === 0) { setRows([]); return; }
    setLoading(true);

    const { start, end } = getPeriodRange(period);
    const results: OperatorRow[] = [];

    // Fetch existing settlements
    const { data: settlements } = await supabase.from('settlements')
      .select('*')
      .eq('business_id', businessId)
      .eq('period_start', start)
      .eq('period_end', end);

    const settlementMap = new Map((settlements ?? []).map(s => [s.operator_id, s]));

    for (const op of operators) {
      const existing = settlementMap.get(op.id) as Settlement | undefined;
      if (existing) {
        results.push({
          id: op.id,
          name: `${op.firstName} ${op.lastName}`,
          services: existing.completed_bookings,
          revenue: existing.total_services,
          tips: existing.total_tips,
          houseCommission: existing.house_commission,
          houseTipShare: existing.house_tip_share,
          chairRent: existing.chair_rent,
          netToOperator: existing.net_to_operator,
          netToHouse: existing.net_to_house,
          status: existing.status,
          settlementId: existing.id,
        });
      } else {
        // Calculate live
        const { data: calc } = await supabase.rpc('calculate_settlement', {
          p_business_id: businessId,
          p_operator_id: op.id,
          p_period_start: start,
          p_period_end: end,
        });
        if (calc && !calc.error) {
          results.push({
            id: op.id,
            name: `${op.firstName} ${op.lastName}`,
            services: calc.completed_bookings ?? 0,
            revenue: calc.total_services ?? 0,
            tips: calc.total_tips ?? 0,
            houseCommission: calc.house_commission ?? 0,
            houseTipShare: calc.house_tip_share ?? 0,
            chairRent: calc.chair_rent ?? 0,
            netToOperator: calc.net_to_operator ?? 0,
            netToHouse: calc.net_to_house ?? 0,
            status: 'live',
            settlementId: null,
          });
        }
      }
    }

    if (mountedRef.current) { setRows(results); setLoading(false); }
  }, [businessId, operators, period]);

  useEffect(() => {
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenerateError(null);
    const { start, end } = getPeriodRange(period);
    const failures: string[] = [];

    for (const row of rows) {
      if (row.settlementId) continue; // already has a settlement
      const { error: upsertErr } = await supabase.from('settlements').upsert({
        business_id: businessId,
        operator_id: row.id,
        period_start: start,
        period_end: end,
        total_services: row.revenue,
        total_tips: row.tips,
        house_commission: row.houseCommission,
        house_tip_share: row.houseTipShare,
        operator_earnings: row.revenue - row.houseCommission + row.tips - row.houseTipShare,
        chair_rent: row.chairRent,
        net_to_operator: row.netToOperator,
        net_to_house: row.netToHouse,
        completed_bookings: row.services,
        status: 'draft',
      }, { onConflict: 'business_id,operator_id,period_start,period_end' });
      if (upsertErr) {
        log.error('settlements.upsert failed for operator', upsertErr, { operatorId: row.id });
        failures.push(row.name);
      }
    }

    setGenerating(false);
    if (failures.length > 0) {
      setGenerateError(`Failed to generate settlements for: ${failures.join(', ')}`);
    }
    void fetchData();
  }, [businessId, rows, period, fetchData]);

  const handleMarkPaid = useCallback(async (settlementId: string) => {
    await supabase.from('settlements')
      .update({ status: 'paid', paid_at: new Date().toISOString() })
      .eq('id', settlementId);
    void fetchData();
  }, [fetchData]);

  const handleExportCSV = useCallback(() => {
    const headers = ['Name', 'Services', 'Revenue', 'Tips', 'House Commission', 'House Tip Share', 'Chair Rent', 'Net to Operator', 'Net to House', 'Status'];
    const csvRows = rows.map(r => [
      r.name, r.services, canonicalMoney(r.revenue), canonicalMoney(r.tips),
      canonicalMoney(r.houseCommission), canonicalMoney(r.houseTipShare), canonicalMoney(r.chairRent),
      canonicalMoney(r.netToOperator), canonicalMoney(r.netToHouse), r.status,
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settlements-${getPeriodRange(period).start}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, period]);

  const totalRevenue = Number(sumMoney(rows.map(r => canonicalMoney(r.revenue))));
  const totalTips = Number(sumMoney(rows.map(r => canonicalMoney(r.tips))));
  const totalHouse = Number(sumMoney(rows.map(r => canonicalMoney(r.netToHouse))));
  const totalTeam = Number(sumMoney(rows.map(r => canonicalMoney(r.netToOperator))));

  const PERIODS: { key: Period; label: string }[] = [
    { key: 'this_week', label: 'This Week' },
    { key: 'last_week', label: 'Last Week' },
    { key: 'this_month', label: 'This Month' },
  ];

  return (
    <div>
      {/* Period selector + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, fontFamily,
                border: period === p.key ? `1px solid ${palette.accentDeep}` : `1px solid ${palette.g200}`,
                background: period === p.key ? palette.accentWash : palette.white,
                color: period === p.key ? palette.accentDeep : palette.g600,
                cursor: 'pointer',
              }}
            >{p.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleGenerate} disabled={generating} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily }}>
            <RefreshCw size={12} /> {generating ? 'Generating...' : 'Generate Settlements'}
          </button>
          <button onClick={handleExportCSV} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily }}>
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {generateError && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12 }}>
          {generateError}
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, padding: '14px 16px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={LABEL}>Total Revenue</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.g800, marginTop: 4 }}>{fmtCurrency(totalRevenue)}</div>
        </div>
        <div style={{ flex: 1, padding: '14px 16px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={LABEL}>Total Tips</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.g800, marginTop: 4 }}>{fmtCurrency(totalTips)}</div>
        </div>
        <div style={{ flex: 1, padding: '14px 16px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={LABEL}>House Total</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.g800, marginTop: 4 }}>{fmtCurrency(totalHouse)}</div>
        </div>
        <div style={{ flex: 1, padding: '14px 16px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={LABEL}>Team Total</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: palette.green, marginTop: 4 }}>{fmtCurrency(totalTeam)}</div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${palette.g200}` }}>
              <th style={{ ...HEADER_CELL, textAlign: 'left' }}>Name</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Services</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Revenue</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Tips</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Commission</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Rent</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>Operator</th>
              <th style={{ ...HEADER_CELL, textAlign: 'right' }}>House</th>
              <th style={{ ...HEADER_CELL, textAlign: 'center' }}>Status</th>
              <th style={{ ...HEADER_CELL, textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ ...CELL, textAlign: 'center', color: palette.g400 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} style={{ ...CELL, textAlign: 'center', color: palette.g400 }}>No operators found</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${palette.g100}` : 'none' }}>
                <td style={{ ...CELL, fontWeight: 500, color: palette.g800 }}>{r.name}</td>
                <td style={{ ...CELL, textAlign: 'right' }}>{r.services}</td>
                <td style={{ ...CELL, textAlign: 'right' }}>{fmtCurrency(r.revenue)}</td>
                <td style={{ ...CELL, textAlign: 'right' }}>{fmtCurrency(r.tips)}</td>
                <td style={{ ...CELL, textAlign: 'right' }}>{fmtCurrency(r.houseCommission + r.houseTipShare)}</td>
                <td style={{ ...CELL, textAlign: 'right' }}>{r.chairRent > 0 ? fmtCurrency(r.chairRent) : '—'}</td>
                <td style={{ ...CELL, textAlign: 'right', fontWeight: 600, color: palette.green }}>{fmtCurrency(r.netToOperator)}</td>
                <td style={{ ...CELL, textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(r.netToHouse)}</td>
                <td style={{ ...CELL, textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: r.status === 'paid' ? palette.greenWash : r.status === 'confirmed' ? palette.amberWash : r.status === 'draft' ? palette.blueWash : palette.g100,
                    color: r.status === 'paid' ? palette.green : r.status === 'confirmed' ? palette.amber : r.status === 'draft' ? palette.blue : palette.g500,
                  }}>{r.status === 'live' ? 'Live' : r.status.toUpperCase()}</span>
                </td>
                <td style={{ ...CELL, textAlign: 'center' }}>
                  {r.settlementId && r.status !== 'paid' && (
                    <button onClick={() => handleMarkPaid(r.settlementId!)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4,
                      border: 'none', background: palette.greenWash, color: palette.green,
                      fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily,
                    }}>
                      <CheckCircle size={10} /> Paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
